import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { observer, useObserver } from 'mobx-react';
import Drawer from '@material-ui/core/Drawer';
import Slide from '@material-ui/core/Slide';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import InfoIcon from '@material-ui/icons/Info';
import Button from '@material-ui/core/Button';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import Link from 'next/link';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Fab from '@material-ui/core/Fab';
import Chip from '@material-ui/core/Chip';
import Modal from '@material-ui/core/Modal';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import PageStateDialog from './Pages/PageStateDialog';
import CreateIcon from '@material-ui/icons/Create';
import BaseTable from './Media/BaseTable';
import withStores from '../stores/withStores';
import AdaIcon from '../icons/Ada';
import adaTheme from '../theme';
import useI18n from '../i18n/useI18n';

const SIDEBAR_WIDTH = 400;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: 0,
    padding: theme.spacing(1),
    zIndex: props => (props.sidebarOpen && !props.showModal ? 1301 : 1300),
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
    pointerEvents: 'none',
    transform: props =>
      props.sidebarOpen ? `translate(-${SIDEBAR_WIDTH}px, 0)` : 'none',
    transition:
      'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms, top 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
  },
  offset: {
    top: props => props.offset,
  },
  info: {
    marginRight: theme.spacing(1),
    pointerEvents: 'all',
  },
  button: {
    // backgroundColor: '#403CFA',
    color: theme.palette.common.white,
    pointerEvents: 'all',
  },
  changes: {
    marginRight: theme.spacing(1),
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'all',
  },
  msg: {
    marginRight: theme.spacing(1),
    pointerEvents: 'all',
  },
  menuPaper: {
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    pointerEvents: 'all',
  },
  sidebar: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    width: SIDEBAR_WIDTH,
    padding: theme.spacing(3),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
  },
  chipRoot: {
    height: 'auto',
  },
  chipLabel: {
    whiteSpace: 'normal',
    padding: theme.spacing(1, 2),
  },
  previewImageTitle: {
    marginLeft: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  previewImage: {
    height: 250,
    backgroundSize: 'cover',
    backgroundRepeat: 'norepeat',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[400]}`,
    transition: 'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.grey[800],
    },
  },
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '90%',
    height: '90%',
    background: theme.palette.common.white,
    overflow: 'auto',
    transform: 'translate(-50%, -50%)',
  },
}));

const AdminMenu = observer(props => {
  const { appStore } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [pageToEdit, setPageToEdit] = React.useState(null);
  const currentUser = props.loginStore.currentUser;
  const currentPage = props.pagesStore.currentPage;
  const isPageToCreate = currentPage && currentPage.uuid.includes('create-new');
  const currentPageHasChanges = currentPage && currentPage.hasChanges;
  const currentPageIsEditable = appStore.pageIsEditable;
  const hasPageOptions =
    appStore.editMode && currentPage && currentPageIsEditable;
  const triggerOffset = useScrollTrigger();
  const classes = useStyles({ ...props, sidebarOpen, showModal });
  const i18n = useI18n();
  const handleSetPageToEdit = page => {
    setPageToEdit(currentPage);
  };

  const handleCloseEditDialog = () => {
    setPageToEdit(null);
  };

  const handleSave = () => {
    if (isPageToCreate) {
      currentPage.saveNewPage();
    } else {
      currentPage.savePage();
    }
  };
  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    props.loginStore.logoutAsync();
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  const handleOpenSidebar = () => {
    setSidebarOpen(true);
    handleCloseMenu();
  };

  const handleChangeTags = (e, value, reason) => {
    // console.log(reason, value);
    currentPage.setTags(value);
  };

  const handleChagePageData = key => e => {
    currentPage.updatePageData({ [key]: e.target.value });
  };

  const handleSetTitle = e => {
    currentPage.setTitle(e.target.value);
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSelectPreviewImage = item => {
    currentPage.updatePageData({ previewImage: item.uuid });
    handleCloseModal();
  };

  const handleEditPage = () => {
    appStore.setEditMode(true);
  };

  React.useEffect(() => {
    if (props.pagesStore.tags.length === 0) {
      props.pagesStore.loadAllTags();
    }
  });

  if (!currentUser || !currentUser.isAdmin) return null;

  return (
    <MuiThemeProvider theme={adaTheme}>
      <React.Fragment>
        <div className={cx(classes.root, { [classes.offset]: !triggerOffset })}>
          <Slide in={currentPageHasChanges && currentPageIsEditable}>
            <div className={classes.changes}>
              <InfoIcon className={classes.info} />
              <Typography
                variant="body1"
                color="inherit"
                className={classes.msg}
              >
                {isPageToCreate
                  ? i18n.t('adminMenu.newPage')
                  : i18n.t('adminMenu.notSaved')}
              </Typography>
              {hasPageOptions && currentPage.isSaving ? (
                <CircularProgress color="inherit" size={24} />
              ) : (
                currentPageHasChanges && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    onClick={handleSave}
                    disabled={currentPage.isSaving}
                  >
                    {isPageToCreate
                      ? i18n.t('adminMenu.saveNew')
                      : i18n.t('adminMenu.saveChanges')}
                  </Button>
                )
              )}
            </div>
          </Slide>
          <Fab
            classes={{ root: classes.button }}
            color="primary"
            size="small"
            onClick={sidebarOpen ? handleCloseSidebar : handleOpenMenu}
          >
            {sidebarOpen ? (
              <CloseIcon fontSize="small" />
            ) : hasPageOptions ? (
              <CreateIcon />
            ) : (
              <AdaIcon />
            )}
          </Fab>
          <Menu
            classes={{ paper: classes.menuPaper }}
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            onClose={handleCloseMenu}
          >
            <MenuItem>
              <Link href="/admin" passHref>
                <Typography component="a" color="inherit" underline="none">
                  Dashboard
                </Typography>
              </Link>
            </MenuItem>
            {currentPage && appStore.editMode && (
              <MenuItem onClick={handleOpenSidebar}>
                {i18n.t('adminMenu.infos')}
              </MenuItem>
            )}
            {currentPageIsEditable && !appStore.editMode && (
              <MenuItem onClick={handleEditPage}>
                {i18n.t('adminMenu.edit')}
              </MenuItem>
            )}
            {currentPageIsEditable && (
              <MenuItem onClick={handleSetPageToEdit}>
                {i18n.t('adminMenu.editState')}
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              {i18n.t('adminMenu.logout')}
            </MenuItem>
          </Menu>
        </div>
        {hasPageOptions && (
          <Drawer
            open={sidebarOpen}
            anchor="right"
            onClose={handleCloseSidebar}
          >
            <div className={classes.sidebar}>
              <Typography variant="h5" gutterBottom>
                {i18n.t('adminMenu.infos')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {i18n.t('adminMenu.infosDescription')}
              </Typography>
              <form className={classes.form}>
                <TextField
                  label={i18n.t('adminMenu.titleLabel')}
                  variant="outlined"
                  fullWidth
                  value={currentPage.title}
                  onChange={handleSetTitle}
                  margin="normal"
                />
                <Autocomplete
                  multiple
                  id="tags-standard"
                  options={props.pagesStore.tags}
                  getOptionLabel={option => option}
                  filterSelectedOptions
                  freeSolo
                  disableClearable
                  value={currentPage.tags.toJS()}
                  onChange={handleChangeTags}
                  // onInputChange={(e, v) => {console.log(e, v)}}
                  loading={props.pagesStore.tags.length === 0}
                  renderTags={(value, getTagProps) => {
                    return value.map((option, index) => (
                      <Chip
                        label={option}
                        classes={{
                          label: classes.chipLabel,
                          root: classes.chipRoot,
                        }}
                        color="primary"
                        {...getTagProps({ index })}
                      />
                    ));
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={i18n.t('adminMenu.tagsLabel')}
                      placeholder={i18n.t('adminMenu.tagsPlaceholder')}
                      margin="normal"
                      fullWidth
                      helperText={i18n.t('adminMenu.tagsHelperText')}
                    />
                  )}
                />
                <TextField
                  label={i18n.t('adminMenu.description')}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  value={currentPage.data.description}
                  defaultValue={currentPage.data.abstract}
                  onChange={handleChagePageData('description')}
                  margin="normal"
                />
                <TextField
                  label={i18n.t('adminMenu.author')}
                  variant="outlined"
                  fullWidth
                  value={currentPage.data.author}
                  onChange={handleChagePageData('author')}
                  margin="normal"
                />

                <Typography
                  variant="caption"
                  className={classes.previewImageTitle}
                  color="textSecondary"
                >
                  {i18n.t('adminMenu.thumbnail')}
                </Typography>
                <div
                  onClick={handleOpenModal}
                  className={classes.previewImage}
                  style={{
                    backgroundImage: `url(/ada-assets/${currentPage.data.previewImage}?w=300&h=300)`,
                  }}
                />
                <TextField
                  disabled
                  label="uuid"
                  variant="outlined"
                  fullWidth
                  value={currentPage.uuid}
                  margin="normal"
                />
                <Modal
                  open={showModal}
                  onClose={handleCloseModal}
                  disableEnforceFocus={false}
                  disableRestoreFocus={false}
                  onClick={e => {
                    // we need to do that to prevent atomic block loose focus on interaction within the modal
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <div className={classes.modalContent}>
                    <BaseTable onSelectItem={handleSelectPreviewImage} />
                  </div>
                </Modal>
              </form>
            </div>
          </Drawer>
        )}
      </React.Fragment>
      {currentPageIsEditable && pageToEdit && (
        <PageStateDialog page={pageToEdit} onClose={handleCloseEditDialog} />
      )}
    </MuiThemeProvider>
  );
});

AdminMenu.propTypes = {
  offset: PropTypes.number,
};

AdminMenu.defaultProps = {
  offset: 80,
};

const AdminMenuWithTheme = props => (
  <MuiThemeProvider theme={adaTheme}>
    <AdminMenu {...props} />
  </MuiThemeProvider>
);

export default withStores(
  'pagesStore',
  'loginStore',
  'appStore',
)(AdminMenuWithTheme);
